"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { getBaseImageUrl } from "@/constants/hooks";
import {
  EventBannerResponse,
  PromotionBannerResponse,
  SubPromotionBanner,
} from "@/services/dataTypes";
import {
  faChevronLeft,
  faChevronRight,
  FontAwesomeIcon,
} from "@/constants/icons";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface Props {
  domain: string;
  banners: EventBannerResponse[] | PromotionBannerResponse[] | SubPromotionBanner[];
  eventName: string;
  breadcrumbs: BreadcrumbItem[];
}

// ─── Breadcrumb bar — sits outside/above the image ─────────────────────────
const BreadcrumbBar = ({ breadcrumbs }: { breadcrumbs: BreadcrumbItem[] }) => (
  <div className="w-full bg-white border-b border-[#E5E7EB] px-4 md:px-8 py-2.5 mt-2">
    <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto">
      <ol className="flex items-center flex-wrap gap-1">
        {breadcrumbs.map((crumb, idx) => {
          const isLast = idx === breadcrumbs.length - 1;
          const isFirst = idx === 0;
          return (
            <li key={idx} className="flex items-center">
              {!isFirst && (
                <svg
                  className="w-3 h-3 text-[#F97316] mx-1.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              {crumb.href && !isLast ? (
                <Link
                  href={crumb.href}
                  className="inline-flex items-center gap-1 text-xs md:text-sm font-medium text-[#1E3A5F] hover:text-[#F97316] transition-colors no-underline group"
                >
                  {isFirst && (
                    <svg
                      className="w-3.5 h-3.5 flex-shrink-0 text-[#1E3A5F] group-hover:text-[#F97316] transition-colors"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                  )}
                  <span className="capitalize">{crumb.label}</span>
                </Link>
              ) : (
                <span
                  className={`text-xs md:text-sm font-semibold capitalize ${isLast ? "text-[#F97316]" : "text-[#1E3A5F]"
                    }`}
                >
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  </div>
);

// ─── Overlay: title + accent at the bottom of the image ────────────────────
const TitleOverlay = ({ eventName }: { eventName: string }) => (
  <div className="absolute inset-0 z-20 flex flex-col justify-end pointer-events-none">
    {/* Uniform tint across the whole image */}
    <div className="absolute inset-0 bg-[#142A45]/30" />
    {/* Bottom gradient for text legibility */}
    <div className="relative bg-gradient-to-t from-[#142A45]/80 via-[#142A45]/20 to-transparent pt-10 pb-6 px-4 md:px-8">
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight tracking-tight drop-shadow-lg max-w-2xl mb-3">
        {eventName}
      </h1>
      <div className="flex items-center gap-2">
        <div className="w-12 h-1 rounded-full bg-[#F97316]" />
        <div className="w-6 h-1 rounded-full bg-[#EA580C]" />
      </div>
    </div>
  </div>
);

const EventHeroBreadcrumb = ({
  domain,
  banners,
  eventName,
  breadcrumbs,
}: Props) => {
  const [hydrated, setHydrated] = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1 },
  });

  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    if (!hydrated || !slider.current || banners.length <= 1) return;
    autoplayRef.current = setInterval(() => {
      slider.current?.next();
    }, 4500);
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [hydrated, slider, banners.length]);

  const stopAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
  };
  const startAutoplay = () => {
    if (!slider.current || banners.length <= 1) return;
    autoplayRef.current = setInterval(() => {
      slider.current?.next();
    }, 4500);
  };

  // ── Skeleton ──────────────────────────────────────────────────────────────
  if (!hydrated) {
    return (
      <div className="w-full mt-[65px] md:mt-[100px] [body:has(#t20-announcement-bar)_&]:mt-[95px] [body:has(#t20-announcement-bar)_&]:md:mt-[135px]">
        <BreadcrumbBar breadcrumbs={breadcrumbs} />
        <div
          className="relative w-full overflow-hidden bg-[#142A45] animate-pulse"
          style={{ aspectRatio: "3/1" }}
        />
      </div>
    );
  }

  // ── Single banner ─────────────────────────────────────────────────────────
  if (banners.length === 1) {
    const src = getBaseImageUrl(domain, banners[0]?.banner, "");
    return (
      <div className="w-full mt-[65px] md:mt-[100px] [body:has(#t20-announcement-bar)_&]:mt-[95px] [body:has(#t20-announcement-bar)_&]:md:mt-[135px]">
        <BreadcrumbBar breadcrumbs={breadcrumbs} />
        <div className="relative w-full overflow-hidden">
          <Image
            src={src}
            alt={`${eventName} Banner`}
            width={1400}
            height={480}
            priority
            className="w-full h-auto block"
          />
          <TitleOverlay eventName={eventName} />
        </div>
      </div>
    );
  }

  // ── Multi-banner slider ───────────────────────────────────────────────────
  return (
    <div className="w-full mt-[65px] md:mt-[100px] [body:has(#t20-announcement-bar)_&]:mt-[95px] [body:has(#t20-announcement-bar)_&]:md:mt-[135px]">
      <BreadcrumbBar breadcrumbs={breadcrumbs} />
      <div
        className="relative w-full overflow-hidden group"
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
      >
        <div ref={sliderRef} className="keen-slider">
          {banners.map((slide, i) => {
            const src = getBaseImageUrl(domain, slide?.banner, "");
            return (
              <div key={i} className="keen-slider__slide">
                <Image
                  src={src}
                  alt={`${eventName} Banner ${i + 1}`}
                  width={1400}
                  height={480}
                  priority={i === 0}
                  className="w-full h-auto block"
                />
              </div>
            );
          })}
        </div>

        <TitleOverlay eventName={eventName} />

        {/* Prev arrow */}
        <button
          type="button"
          onClick={() => slider.current?.prev()}
          aria-label="Previous slide"
          className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-black/25 backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/50"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
        </button>

        {/* Next arrow */}
        <button
          type="button"
          onClick={() => slider.current?.next()}
          aria-label="Next slide"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-black/25 backdrop-blur-sm border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/50"
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5" />
        </button>

        {/* Dot indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-1.5">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => slider.current?.moveToIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="w-2 h-2 rounded-full bg-white/40 hover:bg-[#F97316] transition-colors duration-200"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventHeroBreadcrumb;
